import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  EditOutlined,
  EnvironmentOutlined,
  FileImageOutlined,
  IdcardOutlined,
  KeyOutlined,
  MinusCircleOutlined,
  PhoneOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { errorMessage } from '../../api/client';
import {
  fetchDocumentObjectUrl,
  useInvestor,
  useResetInvestorPassword,
  useUpdateInvestor,
} from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import { date, dateTime } from '../../utils/format';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import DocumentCard from '../common/DocumentCard';
import PasswordConfirmModal from '../common/PasswordConfirmModal';
import PersonAvatar from '../common/PersonAvatar';
import { usePasswordConfirm } from '../../utils/confirmWithPassword';
import { useTranslation } from 'react-i18next';

const MOBILE_PATTERN = /^03\d{2}-?\d{7}$/;

/** One labelled value with an icon — the building block of the detail cards. */
function Field({ icon, label, value }: { icon: ReactNode; label: string; value?: string | null }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0' }}>
      <span style={{ color: '#646B72', fontSize: 16, marginTop: 2, flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: '#646B72', lineHeight: 1.4 }}>{label}</div>
        <div style={{ fontSize: 14, color: '#212B36', wordBreak: 'break-word' }}>
          {value?.trim() ? value : <span style={{ color: '#B8BCC9' }}>Not provided</span>}
        </div>
      </div>
    </div>
  );
}

/** Small pill showing at a glance whether a document is on file. */
function DocumentBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <Tooltip title={ok ? `${label} on file` : `${label} missing`}>
      <Tag
        icon={ok ? <CheckCircleFilled /> : <MinusCircleOutlined />}
        color={ok ? 'success' : 'default'}
        style={{ marginInlineEnd: 6 }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
}

export default function ProfileTab({ investorId }: { investorId: number }) {
  const { t } = useTranslation();
  const { data, isLoading } = useInvestor(investorId);
  const { canWrite, hasRole } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const editPaymentMethod = Form.useWatch('paymentMethod', form) as string | undefined;
  const update = useUpdateInvestor(investorId);
  const resetPassword = useResetInvestorPassword(investorId);
  const passwordConfirm = usePasswordConfirm();
  const isAdmin = hasRole('Admin');

  if (isLoading || !data) return <Skeleton active avatar paragraph={{ rows: 8 }} />;

  const docs = data.documents;
  const isActive = data.status === 'Active';

  const onFinish = async (values: Record<string, unknown>) => {
    if (!(await passwordConfirm.confirm('save these changes'))) return;
    try {
      await update.mutateAsync(values);
      message.success('Investor updated.');
      setEditing(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const onResetPassword = () =>
    Modal.confirm({
      title: 'Re-issue login password?',
      content: `${data.fullName} will be signed out everywhere and must use the new password to log in again. The current password cannot be recovered — it is stored only as a hash.`,
      okText: 'Re-issue password',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await resetPassword.mutateAsync();
          Modal.success({
            title: 'New login credentials',
            width: 480,
            content: (
              <div>
                <p>
                  Username: <strong>{result.username}</strong>
                </p>
                <p>
                  New password: <strong>{result.generatedPassword}</strong>
                </p>
                <p style={{ color: '#FF0000' }}>Save this password now — it will not be shown again.</p>
              </div>
            ),
          });
        } catch (err) {
          // Shown as a modal, not a toast: a toast fires behind the confirm dialog's mask and is
          // easy to miss entirely, which leaves the action looking like it silently did nothing.
          Modal.error({ title: 'Could not re-issue the password', content: errorMessage(err) });
        }
      },
    });

  return (
    <>
      {/* ---------- header banner ---------- */}
      <Card
        style={{
          marginBottom: 16,
          background: 'linear-gradient(135deg, #FFF6EE 0%, #FAFBFE 100%)',
          border: '1px solid #E6EAED',
        }}
        styles={{ body: { padding: 20 } }}
      >
        <Row gutter={[20, 16]} align="middle" wrap>
          <Col flex="none">
            <PersonAvatar
              hasPhoto={docs.hasPhoto}
              fullName={data.fullName}
              fetchUrl={() => fetchDocumentObjectUrl(investorId, 'photo')}
              size={96}
            />
          </Col>

          <Col flex="auto" style={{ minWidth: 220 }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space wrap align="center">
                <Typography.Title level={4} style={{ margin: 0 }}>
                  <span data-no-translate>{data.fullName}</span>
                </Typography.Title>
                <Tag color={isActive ? 'green' : 'default'} style={{ fontWeight: 500 }}>
                  {data.status}
                </Tag>
              </Space>

              <Typography.Text type="secondary">
                {data.fatherName ? `s/o ${data.fatherName}` : 'Investor'}
              </Typography.Text>

              <Space size={16} wrap style={{ marginTop: 4, color: '#646B72' }}>
                <span>
                  <IdcardOutlined /> <strong>{data.investorCode}</strong>
                </span>
                {data.mobile && (
                  <span>
                    <PhoneOutlined /> {data.mobile}
                  </span>
                )}
                <span>
                  <CalendarOutlined /> Joined {date(data.createdAt)}
                </span>
              </Space>

              <div style={{ marginTop: 8 }}>
                <DocumentBadge ok={docs.hasPhoto} label={t('party.photo')} />
                <DocumentBadge ok={docs.hasCnicImage} label={t('party.cnic')} />
                <DocumentBadge ok={docs.hasAgreementVideo || docs.hasAgreementImage} label={t('page.agreement')} />
              </div>
            </Space>
          </Col>

          {canWrite && (
            <Col flex="none">
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  form.setFieldsValue(data);
                  setEditing(true);
                }}
              >
                Edit profile
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* ---------- details ---------- */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <UserOutlined />
                {t('page.personalInformation')}
              </Space>
            }
            size="small"
          >
            <Field icon={<UserOutlined />} label={t('party.fullName')} value={data.fullName} />
            <Field icon={<TeamOutlined />} label={t('party.fatherName')} value={data.fatherName} />
            <Field icon={<PhoneOutlined />} label={t('party.mobile')} value={data.mobile} />
            <Field icon={<IdcardOutlined />} label={t('party.cnic')} value={data.cnic} />
            <Field icon={<EnvironmentOutlined />} label={t('party.address')} value={data.address} />
            <Field icon={<FileImageOutlined />} label={t('common.notes')} value={data.notes} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BankOutlined />
                {t('page.accountAccess')}
              </Space>
            }
            size="small"
            /* Only an admin may mint credentials for someone else's account — and the investor
               viewing their own profile in the portal must never see this control at all. */
            extra={
              isAdmin ? (
                <Button size="small" icon={<KeyOutlined />} loading={resetPassword.isPending} onClick={onResetPassword}>
                  Re-issue password
                </Button>
              ) : undefined
            }
          >
            <Field icon={<IdcardOutlined />} label={t('page.investorCode')} value={data.investorCode} />
            <Field icon={<BankOutlined />} label={t('finance.paymentMethod')} value={data.paymentMethod} />
            <Field icon={<BankOutlined />} label={t('finance.accountNumber')} value={data.bankAccount} />
            <Field icon={<KeyOutlined />} label={t('page.loginUsername')} value={data.username} />
            <Field icon={<CalendarOutlined />} label={t('table.created')} value={dateTime(data.createdAt)} />
            <Field icon={<CalendarOutlined />} label={t('page.lastUpdated')} value={dateTime(data.updatedAt)} />
            {/* Block-level footer with its own rule — as a bare inline <Text> this note ran past the
                card's bottom edge instead of sitting inside it. */}
            {isAdmin && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #E6EAED' }}>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', lineHeight: 1.5 }}>
                  Passwords are stored hashed and cannot be looked up. If the investor has lost theirs, re-issue a new
                  one above.
                </Typography.Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title={
              <Space>
                <FileImageOutlined />
                Documents
              </Space>
            }
            size="small"
            extra={
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Captured at account opening{docs.updatedAt ? ` · ${dateTime(docs.updatedAt)}` : ''}
              </Typography.Text>
            }
          >
            {/*
              The profile photo is deliberately absent here — it is already shown as the avatar in
              the header above, and repeating it served no purpose.
            */}
            {/* Narrow columns keep these as compact reference thumbnails rather than letting a
                small scan stretch across half the page. */}
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12} lg={7} xl={6}>
                <DocumentCard
                  label={t('party.cnicImage')}
                  exists={docs.hasCnicImage}
                  fetchUrl={() => fetchDocumentObjectUrl(investorId, 'cnic-image')}
                />
              </Col>
              <Col xs={24} sm={12} lg={7} xl={6}>
                <DocumentCard
                  label={t('party.agreementVideo')}
                  exists={docs.hasAgreementVideo}
                  isVideo
                  fetchUrl={() => fetchDocumentObjectUrl(investorId, 'agreement-video')}
                />
              </Col>
              <Col xs={24} sm={12} lg={7} xl={6}>
                <DocumentCard
                  label={t('party.agreementImage')}
                  exists={docs.hasAgreementImage}
                  fetchUrl={() => fetchDocumentObjectUrl(investorId, 'agreement-image')}
                />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* ---------- edit ---------- */}
      <Modal maskClosable={false}
        title="Edit investor"
        open={editing}
        onCancel={() => setEditing(false)}
        onOk={() => form.submit()}
        confirmLoading={update.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
          <Form.Item name="fullName" label={t('party.fullName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fatherName" label={t('party.fatherName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="mobile"
            label={t('party.mobile')}
            rules={[{ required: true }, { pattern: MOBILE_PATTERN, message: 'Enter an 11-digit mobile number' }]}
          >
            <Input placeholder="03001234567" />
          </Form.Item>
          {/* CNIC identifies the investor and is fixed once the account is opened. */}
          <Form.Item name="cnic" label={t('party.cnic')} help="CNIC cannot be changed after the account is created.">
            <Input disabled />
          </Form.Item>
          <Form.Item name="address" label={t('party.address')} rules={[{ required: true }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="paymentMethod" label={t('finance.paymentMethod')} rules={[{ required: true }]}>
            <Select placeholder="Select a method" options={paymentMethodOptions} />
          </Form.Item>
          {editPaymentMethod !== 'Cash' && (
            <Form.Item name="bankAccount" label={t('finance.accountNumber')} rules={[{ required: true }]}>
              <Input placeholder="Account title, IBAN or wallet number" />
            </Form.Item>
          )}
          <Form.Item name="notes" label={t('common.notes')}>
            <Input.TextArea rows={2} placeholder="Optional notes about this account" />
          </Form.Item>
        </Form>
      </Modal>
      <PasswordConfirmModal state={passwordConfirm.state} />
    </>
  );
}
